/**
 * Example payloads for each webhook event, shown in the dashboard so an integrator can see the
 * shape of what they will receive before wiring anything up.
 *
 * <p>These mirror the backend event classes. They are illustrative rather than generated, so if an
 * event class gains a field this file should be updated alongside it.
 *
 * <p>Amounts are in minor units throughout, matching the rest of the gift card API.
 */

/** Shared gift card order body, used by the order lifecycle events. */
const giftCardOrder = {
  eventType: 'GIFT_CARD_CREATED',
  publisherId: '65f1c2a4e8b3d91a7c4f0e21',
  orderId: '66a7d3b1f2c84e5a9b0d7f33',
  clientOrderId: 'order-2026-09-24-0001',
  endUserId: 'user_8842',
  brandId: '6ab3a312d0f22bbb460ab7b7',
  brandCode: 'AMZNCOM',
  brandName: 'Amazon.com',
  brandImageUrl: 'https://media.boogiesearch.com/gift-cards/brands/amzngb/logo.png',
  priceInCents: 2500,
  costInCents: 2500,
  marginInCents: 50,
  publisherCommissionInCents: 40,
  publisherShare: 0.8,
  ratePercent: 2,
  currency: 'GBP',
  status: 'PENDING',
  giftLink: null,
  failureMessage: null,
  expiryDays: 365,
  expiresOn: 1821769200000,
  refundedOn: null,
  createdOn: 1790257262118,
  fulfilledOn: null,
}

/** Shared brand body, used by the brand catalogue events. */
const giftCardBrand = {
  eventType: 'GIFT_CARD_BRAND_ENABLED',
  publisherId: '65f1c2a4e8b3d91a7c4f0e21',
  brandId: '6ab3a312d0f22bbb460ab7b7',
  brandCode: 'AMZNCOM',
  brandName: 'Amazon.com',
  brandImageUrl: 'https://media.boogiesearch.com/gift-cards/brands/amzngb/logo.png',
  currency: 'GBP',
  minPriceInCents: 100,
  maxPriceInCents: 50000,
  allowedValuesInCents: null,
  expiryDays: 365,
  changes: [],
  createdOn: 1790257262118,
}

/** Shared balance body, used by the balance events. */
const giftCardBalance = {
  eventType: 'GIFT_CARD_BALANCE_DEPOSITED',
  publisherId: '65f1c2a4e8b3d91a7c4f0e21',
  currency: 'GBP',
  balanceInCents: 1000000,
  amountInCents: 1000000,
  reference: 'BACS-2026-09-24-001',
  affordableFaceValueInCents: 1000000,
  requiredInCents: null,
  thresholdInCents: null,
  queuedOrderCount: null,
  orderId: null,
  ledgerEntryId: '66a7d3b1f2c84e5a9b0d7f01',
  ledgerEntryType: 'DEPOSIT',
  createdOn: 1790254514902,
}

/** Shared cashback body, used by the cashback lifecycle events. */
const cashback = {
  publisherId: '65f1c2a4e8b3d91a7c4f0e21',
  cashbackId: 'test_cashbackObject_12',
  endUserId: 'test_user_34',
  transactionId: 'test_transaction_56',
  offerId: 'test_offer_78',
  retailerName: 'Test Retailer',
  retailerWebsite: 'https://boogi.io',
  logo: 'https://placehold.co/32x32',
  cashbackStatus: 'CREATED',
  declinedReason: null,
}

/** Shared offer body, used by the offer lifecycle events. */
const offer = {
  publisherId: '65f1c2a4e8b3d91a7c4f0e21',
  offerId: 'test_offer_78',
  offerName: 'Test Retailer',
  retailerName: 'Test Retailer',
  retailerWebsite: 'https://boogi.io',
  logo: 'https://placehold.co/32x32',
}

/** Shared tier body, used by the tier and user events. */
const tier = {
  publisherId: '65f1c2a4e8b3d91a7c4f0e21',
  tierId: 'test_tier_01',
  tierName: 'Gold',
  endUserId: 'test_user_34',
}

/**
 * Example payload per event type, keyed by the event's enum name.
 *
 * <p>An event with no entry here simply shows no example, so a newly added event degrades to
 * "no example available" rather than breaking the page.
 */
export const WEBHOOK_EXAMPLES = {
  // ── Cashback ──────────────────────────────────────────────
  CASHBACK_CREATED: { ...cashback, eventType: 'CASHBACK_CREATED', cashbackStatus: 'CREATED' },
  CASHBACK_REFUNDED: { ...cashback, eventType: 'CASHBACK_REFUNDED', cashbackStatus: 'REFUNDED' },
  CASHBACK_BEING_VERIFIED: {
    ...cashback,
    eventType: 'CASHBACK_BEING_VERIFIED',
    cashbackStatus: 'BEING_VERIFIED',
  },
  CASHBACK_VERIFIED: { ...cashback, eventType: 'CASHBACK_VERIFIED', cashbackStatus: 'VERIFIED' },
  CASHBACK_DECLINED: {
    ...cashback,
    eventType: 'CASHBACK_DECLINED',
    cashbackStatus: 'DECLINED',
    declinedReason: 'A declined reason will go here',
  },
  CASHBACK_RECEIVED: { ...cashback, eventType: 'CASHBACK_RECEIVED', cashbackStatus: 'RECEIVED' },
  CASHBACK_PAID_TO_PUBLISHER: {
    ...cashback,
    eventType: 'CASHBACK_PAID_TO_PUBLISHER',
    cashbackStatus: 'PAID',
  },
  CASHBACK_DISQUALIFIED: {
    ...cashback,
    eventType: 'CASHBACK_DISQUALIFIED',
    cashbackStatus: 'DISQUALIFIED',
    declinedReason: 'A disqualified reason will go here',
  },

  // ── Offers ────────────────────────────────────────────────
  OFFER_APPROVED: { ...offer, eventType: 'OFFER_APPROVED' },
  OFFER_REMOVED_FROM_PLATFORM: { ...offer, eventType: 'OFFER_REMOVED_FROM_PLATFORM' },
  OFFER_REMOVED_FROM_PROGRAM: { ...offer, eventType: 'OFFER_REMOVED_FROM_PROGRAM' },
  OFFER_UPDATED: { ...offer, eventType: 'OFFER_UPDATED' },
  OFFER_ACTIVATED: { ...offer, eventType: 'OFFER_ACTIVATED' },
  OFFER_DEACTIVATED: { ...offer, eventType: 'OFFER_DEACTIVATED' },
  CLICK_TO_ACTIVATE_OFFER_EXPIRING_SOON: {
    ...offer,
    eventType: 'CLICK_TO_ACTIVATE_OFFER_EXPIRING_SOON',
    expiresOn: 1790812800000,
    daysRemaining: 7,
  },
  CLICK_TO_ACTIVATE_OFFER_EXPIRED: {
    ...offer,
    eventType: 'CLICK_TO_ACTIVATE_OFFER_EXPIRED',
    expiredOn: 1790208000000,
  },

  // ── Tiers and users ───────────────────────────────────────
  TIER_CREATED: { ...tier, eventType: 'TIER_CREATED' },
  TIER_REMOVED: { ...tier, eventType: 'TIER_REMOVED' },
  USER_ADDED_TO_TIER: { ...tier, eventType: 'USER_ADDED_TO_TIER' },
  USER_REMOVED_FROM_TIER: { ...tier, eventType: 'USER_REMOVED_FROM_TIER' },

  // ── Gift cards ────────────────────────────────────────────
  GIFT_CARD_CREATED: { ...giftCardOrder, eventType: 'GIFT_CARD_CREATED', status: 'PENDING' },
  GIFT_CARD_STATUS_UPDATED: {
    ...giftCardOrder,
    eventType: 'GIFT_CARD_STATUS_UPDATED',
    status: 'PROCESSING',
  },
  GIFT_CARD_FULFILLED: {
    ...giftCardOrder,
    eventType: 'GIFT_CARD_FULFILLED',
    status: 'FULFILLED',
    giftLink: 'https://reward.example.com/redeem/abc123',
    fulfilledOn: 1790257263442,
  },
  GIFT_CARD_FAILED: {
    ...giftCardOrder,
    eventType: 'GIFT_CARD_FAILED',
    status: 'FAILED',
    failureMessage: 'The reward could not be created. Please try again.',
  },
  GIFT_CARD_REFUNDED: {
    ...giftCardOrder,
    eventType: 'GIFT_CARD_REFUNDED',
    status: 'CANCELED',
    refundedOn: 1790258531007,
  },
  GIFT_CARD_BALANCE_DEPOSITED: {
    ...giftCardBalance,
    eventType: 'GIFT_CARD_BALANCE_DEPOSITED',
  },
  GIFT_CARD_BALANCE_LOW: {
    ...giftCardBalance,
    eventType: 'GIFT_CARD_BALANCE_LOW',
    balanceInCents: 1500,
    amountInCents: -2500,
    reference: null,
    affordableFaceValueInCents: 1500,
    requiredInCents: 2500,
    orderId: '66a7d3b1f2c84e5a9b0d7f33',
    ledgerEntryId: null,
    ledgerEntryType: null,
  },
  GIFT_CARD_BALANCE_THRESHOLD_REACHED: {
    ...giftCardBalance,
    eventType: 'GIFT_CARD_BALANCE_THRESHOLD_REACHED',
    balanceInCents: 48000,
    amountInCents: -2500,
    reference: null,
    affordableFaceValueInCents: 48000,
    thresholdInCents: 50000,
    orderId: '66a7d3b1f2c84e5a9b0d7f33',
    ledgerEntryId: null,
    ledgerEntryType: null,
  },
  GIFT_CARD_QUEUE_FROZEN: {
    ...giftCardBalance,
    eventType: 'GIFT_CARD_QUEUE_FROZEN',
    balanceInCents: 1500,
    amountInCents: null,
    reference: null,
    affordableFaceValueInCents: 1500,
    // The total outstanding across the queue, not just the order that was refused.
    requiredInCents: 92600,
    queuedOrderCount: 3,
    orderId: null,
    ledgerEntryId: null,
    ledgerEntryType: null,
  },

  GIFT_CARD_BRAND_ENABLED: { ...giftCardBrand, eventType: 'GIFT_CARD_BRAND_ENABLED' },
  GIFT_CARD_BRAND_DISABLED: { ...giftCardBrand, eventType: 'GIFT_CARD_BRAND_DISABLED' },
  GIFT_CARD_BRAND_REMOVED: { ...giftCardBrand, eventType: 'GIFT_CARD_BRAND_REMOVED' },
  GIFT_CARD_BRAND_UPDATED: {
    ...giftCardBrand,
    eventType: 'GIFT_CARD_BRAND_UPDATED',
    changes: [
      { field: 'Fixed denominations', from: '[1000, 2500, 5000]', to: '[1000, 2500, 5000, 10000]' },
    ],
  },
}

/** Returns the example payload for an event type, or null when none is defined. */
export function exampleFor(eventType) {
  return WEBHOOK_EXAMPLES[eventType] || null
}

export default WEBHOOK_EXAMPLES
